import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { 
  Download, Mail, Calendar, Database, FileText, 
  Settings as SettingsIcon, Send, Clock, Shield,
  HardDrive, Archive, AlertCircle, CheckCircle2, CalendarDays,
  Upload, AlertTriangle, RotateCcw, LayoutDashboard, Target,
  FolderKanban, Contact, LayoutGrid, Wrench, BarChart3, UserCog,
  Users as UsersIcon, HelpCircle, Lock, Eye, EyeOff, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { DatePickerField } from '../components/DatePickerField';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

import { AIAssistantSettings } from '../components/settings/AIAssistantSettings';
import { ApiKeysSettings } from '../components/settings/ApiKeysSettings';
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const Settings = () => {
  const { user, updateUser } = useAuth();
  const [weeklyReportEnabled, setWeeklyReportEnabled] = useState(true);
  const [reportEmail, setReportEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingMonthly, setSendingMonthly] = useState(false);
  const [downloadingCSV, setDownloadingCSV] = useState(false);
  const [downloadingJSON, setDownloadingJSON] = useState(false);
  const [hiddenTabs, setHiddenTabs] = useState([]);
  const [settingsTab, setSettingsTab] = useState('general');
  
  // Date range for CSV export
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Restore state
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const fileInputRef = useRef(null);

  // Session settings
  const [sessionTimeout, setSessionTimeout] = useState(8);
  const [sessionWarning, setSessionWarning] = useState(5);
  const [savingSession, setSavingSession] = useState(false);

  // Login appearance (background image)
  const [loginBgUrl, setLoginBgUrl] = useState(null);
  const [uploadingBg, setUploadingBg] = useState(false);
  const loginBgInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
    // Set default dates (last 30 days)
    const today = new Date();
    const monthAgo = new Date();
    monthAgo.setDate(today.getDate() - 30);
    setDateTo(today.toISOString().split('T')[0]);
    setDateFrom(monthAgo.toISOString().split('T')[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      const [reportRes, sessionRes, tabsRes] = await Promise.all([
        axios.get(`${API_URL}/settings/weekly-report`),
        axios.get(`${API_URL}/settings/session`).catch(() => ({ data: { timeout_hours: 8, warning_minutes: 5 } })),
        axios.get(`${API_URL}/users/me/tabs`).catch(() => ({ data: { hidden_tabs: [] } }))
      ]);
      setWeeklyReportEnabled(reportRes.data.enabled);
      setReportEmail(reportRes.data.email || '');
      setSessionTimeout(sessionRes.data.timeout_hours);
      setSessionWarning(sessionRes.data.warning_minutes);
      setHiddenTabs(tabsRes.data.hidden_tabs || []);
      const bgRes = await axios.get(`${API_URL}/settings/login-appearance`).catch(() => ({ data: {} }));
      setLoginBgUrl(bgRes.data?.background_url || null);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginBgUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Wybierz plik graficzny');
      return;
    }
    setUploadingBg(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/settings/login-appearance/background`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setLoginBgUrl(res.data.background_url);
      toast.success('Tło ekranu logowania zaktualizowane');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd wgrywania obrazu');
    } finally {
      setUploadingBg(false);
      if (loginBgInputRef.current) loginBgInputRef.current.value = '';
    }
  };

  const handleLoginBgRemove = async () => {
    try {
      await axios.put(`${API_URL}/settings/login-appearance`, { background_url: null });
      setLoginBgUrl(null);
      toast.success('Przywrócono domyślne tło ekranu logowania');
    } catch (error) {
      toast.error('Błąd przywracania domyślnego tła');
    }
  };

  const saveWeeklyReportSettings = async () => {
    try {
      await axios.post(`${API_URL}/settings/weekly-report`, null, {
        params: {
          enabled: weeklyReportEnabled,
          email: reportEmail
        }
      });
      toast.success('Ustawienia zapisane');
    } catch (error) {
      toast.error('Błąd zapisu ustawień');
    }
  };

  const sendTestReport = async () => {
    setSendingTest(true);
    try {
      await axios.post(`${API_URL}/settings/send-test-report`);
      toast.success(`Raport tygodniowy (CSV) wysłany na ${reportEmail}`);
    } catch (error) {
      toast.error('Błąd wysyłania raportu testowego');
    } finally {
      setSendingTest(false);
    }
  };

  const sendMonthlyReport = async () => {
    setSendingMonthly(true);
    try {
      await axios.post(`${API_URL}/settings/send-test-monthly-report`);
      toast.success(`Raport miesięczny (CSV) wysłany na ${reportEmail}`);
    } catch (error) {
      toast.error('Błąd wysyłania raportu miesięcznego');
    } finally {
      setSendingMonthly(false);
    }
  };

  const downloadCSV = async () => {
    setDownloadingCSV(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await axios.get(`${API_URL}/export/projects-csv?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      let filename = 'zlecenia';
      if (dateFrom && dateTo) {
        filename += `_${dateFrom}_${dateTo}`;
      }
      filename += '.csv';
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Plik CSV pobrany');
    } catch (error) {
      toast.error('Błąd pobierania CSV');
    } finally {
      setDownloadingCSV(false);
    }
  };

  const downloadFullBackup = async () => {
    setDownloadingJSON(true);
    try {
      const response = await axios.get(`${API_URL}/export/full-backup`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trendhomes_backup_${new Date().toISOString().slice(0,19).replace(/:/g, '-')}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Pełna kopia zapasowa pobrana');
    } catch (error) {
      toast.error('Błąd pobierania kopii zapasowej');
    } finally {
      setDownloadingJSON(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        toast.error('Wybierz plik w formacie .json');
        return;
      }
      setSelectedFile(file);
      setRestoreDialogOpen(true);
    }
  };

  const handleRestoreConfirm = async () => {
    if (!selectedFile) return;
    
    setRestoring(true);
    setRestoreResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(`${API_URL}/import/restore-backup`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setRestoreResult(response.data);
      toast.success(response.data.message);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Błąd przywracania kopii zapasowej';
      toast.error(errorMessage);
      setRestoreResult({
        success: false,
        message: errorMessage
      });
    } finally {
      setRestoring(false);
    }
  };

  const handleRestoreCancel = () => {
    setRestoreDialogOpen(false);
    setSelectedFile(null);
    setRestoreResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)] max-w-md">
            <CardContent className="pt-6 text-center">
              <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Brak dostępu</h2>
              <p className="text-[var(--crm-text-secondary)]">Ta strona jest dostępna tylko dla administratorów.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`p-4 lg:p-8 mx-auto ${settingsTab === 'api' ? 'max-w-6xl' : 'max-w-4xl'}`}>
        <div className="flex items-center gap-3 mb-6">
          <SettingsIcon className="w-8 h-8 text-[#FF6B35]" />
          <div>
            <h1 className="text-2xl font-bold text-[var(--crm-text-primary)]">Ustawienia</h1>
            <p className="text-[var(--crm-text-secondary)] text-sm">Zarządzaj automatyzacją i kopiami zapasowymi</p>
          </div>
        </div>

        <div className="flex gap-1 mb-6 p-1 rounded-lg bg-[var(--crm-bg-panel)] border border-[var(--crm-border)] w-fit" data-testid="settings-tabs">
          {[['general', 'Ogólne'], ['api', 'Klucze API']].map(([v, l]) => (
            <button key={v} onClick={() => setSettingsTab(v)} data-testid={`settings-tab-${v}`}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${settingsTab === v ? 'bg-[#FF6B35] text-white' : 'text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)]'}`}>
              {l}
            </button>
          ))}
        </div>

        {settingsTab === 'api' ? <ApiKeysSettings /> : (
        <div className="space-y-6">
          <AIAssistantSettings />

          {/* Login Appearance Section */}
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" data-testid="login-appearance-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#FF6B35]/15 flex items-center justify-center">
                  <ImageIcon className="w-5 h-5 text-[#FF6B35]" />
                </div>
                <div>
                  <CardTitle>Wygląd logowania</CardTitle>
                  <CardDescription className="text-[var(--crm-text-secondary)]">
                    Ustaw własne zdjęcie w tle ekranu logowania
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-[var(--crm-text-secondary)] mb-2 block">Tło ekranu logowania</Label>
                <div className="rounded-lg overflow-hidden border border-[var(--crm-border)] bg-[var(--crm-bg-panel)] relative aspect-[16/6]">
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${loginBgUrl ? (loginBgUrl.startsWith('http') ? loginBgUrl : process.env.REACT_APP_BACKEND_URL + loginBgUrl) : 'https://images.unsplash.com/photo-1640631005057-de521cfcd36e?crop=entropy&cs=srgb&fm=jpg&q=85'})` }}
                    data-testid="login-bg-preview"
                  />
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="text-white/90 text-xs font-medium px-3 py-1 rounded-full bg-black/40">
                      {loginBgUrl ? 'Własne tło (podgląd z nakładką)' : 'Domyślne tło'}
                    </span>
                  </div>
                </div>
                <p className="text-[var(--crm-text-muted)] text-xs mt-2">
                  Na zdjęcie nakładana jest delikatna ciemna warstwa, aby formularz logowania był zawsze czytelny. Zalecane zdjęcie poziome (np. 1920×1080).
                </p>
              </div>

              <input
                ref={loginBgInputRef}
                type="file"
                accept="image/*"
                onChange={handleLoginBgUpload}
                className="hidden"
                data-testid="login-bg-file-input"
              />
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => loginBgInputRef.current?.click()}
                  disabled={uploadingBg}
                  className="bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white"
                  data-testid="login-bg-upload-button"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingBg ? 'Wgrywanie...' : (loginBgUrl ? 'Zmień zdjęcie' : 'Wgraj zdjęcie')}
                </Button>
                {loginBgUrl && (
                  <Button
                    onClick={handleLoginBgRemove}
                    variant="outline"
                    className="border-[var(--crm-border)] text-[var(--crm-text-primary)] hover:bg-[var(--crm-bg-card-hover)]"
                    data-testid="login-bg-remove-button"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Usuń / przywróć domyślne
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Weekly Report Section */}
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="w-6 h-6 text-[#FF6B35]" />
                  <div>
                    <CardTitle>Raport Tygodniowy</CardTitle>
                    <CardDescription className="text-[var(--crm-text-secondary)]">
                      Automatyczny email z podsumowaniem co niedzielę o 22:00
                    </CardDescription>
                  </div>
                </div>
                <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                  <Clock className="w-3 h-3 mr-1" />
                  Niedziela 22:00
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-[var(--crm-bg-main)] rounded-lg">
                <div>
                  <p className="text-[var(--crm-text-primary)] font-medium">Włącz automatyczne raporty</p>
                  <p className="text-[var(--crm-text-secondary)] text-sm">Tygodniowy (niedziela 22:00) + Miesięczny (1-szy każdego miesiąca)</p>
                </div>
                <Switch
                  checked={weeklyReportEnabled}
                  onCheckedChange={setWeeklyReportEnabled}
                  data-testid="weekly-report-switch"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reportEmail" className="text-[var(--crm-text-secondary)]">
                  Adres email dla raportów
                </Label>
                <Input
                  id="reportEmail"
                  type="email"
                  value={reportEmail}
                  onChange={(e) => setReportEmail(e.target.value)}
                  placeholder="contact@trendhomes.pl"
                  className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"
                  data-testid="report-email-input"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={saveWeeklyReportSettings}
                  className="bg-[#FF6B35] hover:bg-[#E55A2B] text-white"
                  data-testid="save-settings-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Zapisz ustawienia
                </Button>
                <Button
                  onClick={sendTestReport}
                  disabled={sendingTest || !reportEmail}
                  variant="outline"
                  className="bg-transparent border-[var(--crm-border)] text-[var(--crm-text-secondary)] hover:bg-[var(--crm-bg-btn-secondary)]"
                  data-testid="send-test-report-btn"
                >
                  {sendingTest ? (
                    <>
                      <div className="w-4 h-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Wysyłanie...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Test tygodniowy
                    </>
                  )}
                </Button>
                <Button
                  onClick={sendMonthlyReport}
                  disabled={sendingMonthly || !reportEmail}
                  variant="outline"
                  className="bg-transparent border-purple-500/50 text-purple-300 hover:bg-purple-500/20"
                  data-testid="send-monthly-report-btn"
                >
                  {sendingMonthly ? (
                    <>
                      <div className="w-4 h-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Wysyłanie...
                    </>
                  ) : (
                    <>
                      <CalendarDays className="w-4 h-4 mr-2" />
                      Test miesięczny
                    </>
                  )}
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-300">
                      <p className="font-medium">Raport Tygodniowy (CSV):</p>
                      <ul className="list-disc list-inside mt-1 text-blue-200/80 text-xs">
                        <li>Zlecenia z ostatnich 7 dni</li>
                        <li>Statusy, marże, linki do protokołów</li>
                        <li>Wysyłany: niedziela 22:00</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <CalendarDays className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-purple-300">
                      <p className="font-medium">Raport Miesięczny (CSV):</p>
                      <ul className="list-disc list-inside mt-1 text-purple-200/80 text-xs">
                        <li>Pełna lista wszystkich zleceń</li>
                        <li>Nazwa, klient, marże, kontakty</li>
                        <li>Wysyłany: 1-szy o 08:00</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Backup Section */}
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Database className="w-6 h-6 text-[#FF6B35]" />
                <div>
                  <CardTitle>Kopie Zapasowe</CardTitle>
                  <CardDescription className="text-[var(--crm-text-secondary)]">
                    Pobierz dane aplikacji w różnych formatach
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* CSV Export with date range */}
              <div className="p-4 bg-[var(--crm-bg-main)] rounded-lg space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-[var(--crm-text-primary)] font-medium">Eksport Zleceń (CSV)</h3>
                </div>
                <p className="text-[var(--crm-text-secondary)] text-sm">
                  Zlecenia w formacie CSV (Excel). Zawiera: nazwę, klienta, status, marże, kontakty, linki do protokołów.
                </p>
                
                {/* Date Range Selection */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="dateFrom" className="text-[var(--crm-text-secondary)] text-xs">Data od</Label>
                    <DatePickerField
                      value={dateFrom}
                      onChange={(val) => setDateFrom(val)}
                      placeholder="Data od"
                      data-testid="date-from-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="dateTo" className="text-[var(--crm-text-secondary)] text-xs">Data do</Label>
                    <DatePickerField
                      value={dateTo}
                      onChange={(val) => setDateTo(val)}
                      placeholder="Data do"
                      data-testid="date-to-input"
                    />
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={downloadCSV}
                    disabled={downloadingCSV}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="download-csv-btn"
                  >
                    {downloadingCSV ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Pobierz CSV
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => {
                      setDateFrom('');
                      setDateTo('');
                    }}
                    variant="outline"
                    className="bg-transparent border-[var(--crm-border)] text-[var(--crm-text-secondary)] hover:bg-[var(--crm-bg-btn-secondary)]"
                  >
                    Wszystkie daty
                  </Button>
                </div>
              </div>

              {/* Full JSON Backup */}
              <div className="p-4 bg-[var(--crm-bg-main)] rounded-lg">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Archive className="w-5 h-5 text-purple-400" />
                      <h3 className="text-[var(--crm-text-primary)] font-medium">Pełna Kopia Zapasowa (JSON)</h3>
                    </div>
                    <p className="text-[var(--crm-text-secondary)] text-sm">
                      Kompletny backup wszystkich danych: zlecenia, zadania montażowe,
                      pracownicy, komentarze, załączniki, role.
                    </p>
                  </div>
                  <Button
                    onClick={downloadFullBackup}
                    disabled={downloadingJSON}
                    className="bg-purple-600 hover:bg-purple-700 text-[var(--crm-text-primary)] whitespace-nowrap"
                    data-testid="download-json-btn"
                  >
                    {downloadingJSON ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <HardDrive className="w-4 h-4 mr-2" />
                        Pełny Backup
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Backup Info */}
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-300">
                    <p className="font-medium">Zalecenia dotyczące kopii zapasowych:</p>
                    <ul className="list-disc list-inside mt-1 text-amber-200/80">
                      <li>Pobieraj kopię zapasową co najmniej raz w tygodniu</li>
                      <li>Przechowuj kopie w bezpiecznym miejscu (np. Google Drive, Dropbox)</li>
                      <li>Plik JSON zawiera pełne dane, CSV tylko zlecenia</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Restore from Backup Section */}
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
            <CardHeader>
              <div className="flex items-center gap-3">
                <RotateCcw className="w-6 h-6 text-[#FF6B35]" />
                <div>
                  <CardTitle>Przywracanie z Kopii Zapasowej</CardTitle>
                  <CardDescription className="text-[var(--crm-text-secondary)]">
                    Przywróć dane systemu z pliku JSON
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-[var(--crm-bg-main)] rounded-lg">
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Upload className="w-5 h-5 text-blue-400" />
                      <h3 className="text-[var(--crm-text-primary)] font-medium">Przywróć system z kopii JSON</h3>
                    </div>
                    <p className="text-[var(--crm-text-secondary)] text-sm mb-4">
                      Wybierz plik .json wygenerowany przez tę aplikację. System przywróci wszystkie dane:
                      zlecenia, zadania montażowe, pracowników, komentarze, załączniki i ustawienia.
                    </p>
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".json"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="restore-file-input"
                    />
                    
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      className="bg-blue-600 hover:bg-blue-700 text-[var(--crm-text-primary)]"
                      data-testid="restore-backup-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Wybierz plik kopii zapasowej
                    </Button>
                  </div>
                </div>
              </div>

              {/* Warning Info */}
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-300">
                    <p className="font-medium">Ostrzeżenie:</p>
                    <ul className="list-disc list-inside mt-1 text-red-200/80">
                      <li>Przywracanie nadpisze istniejące dane z tymi samymi ID</li>
                      <li>Zalecamy wykonanie kopii zapasowej przed przywracaniem</li>
                      <li>Akceptowane są tylko pliki .json wygenerowane przez tę aplikację</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Restore Confirmation Dialog */}
          <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
            <AlertDialogContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] max-w-lg">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-[var(--crm-text-primary)] flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  Potwierdź przywracanie z kopii
                </AlertDialogTitle>
                <AlertDialogDescription className="text-[var(--crm-text-secondary)]">
                  {!restoreResult ? (
                    <>
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 my-3">
                        <p className="text-amber-300 font-medium">
                          UWAGA: Ta operacja nadpisze aktualne dane informacjami z pliku kopii zapasowej.
                        </p>
                      </div>
                      
                      {selectedFile && (
                        <div className="bg-[var(--crm-bg-main)] rounded-lg p-3 mt-3">
                          <p className="text-[var(--crm-text-secondary)] text-sm">
                            <span className="text-[var(--crm-text-muted)]">Wybrany plik:</span>{' '}
                            <span className="font-medium">{selectedFile.name}</span>
                          </p>
                          <p className="text-[var(--crm-text-secondary)] text-sm">
                            <span className="text-[var(--crm-text-muted)]">Rozmiar:</span>{' '}
                            {(selectedFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      )}
                      
                      <p className="mt-4 text-[var(--crm-text-secondary)]">
                        Czy na pewno chcesz kontynuować?
                      </p>
                    </>
                  ) : (
                    <div className="my-3">
                      {restoreResult.success ? (
                        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-emerald-400 mb-3">
                            <CheckCircle2 className="w-5 h-5" />
                            <span className="font-medium">Przywracanie zakończone!</span>
                          </div>
                          <p className="text-emerald-300 text-sm mb-3">{restoreResult.message}</p>
                          
                          {restoreResult.stats && (
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              {Object.entries(restoreResult.stats).map(([key, value]) => (
                                value > 0 && (
                                  <div key={key} className="flex justify-between text-[var(--crm-text-secondary)]">
                                    <span className="capitalize">{key.replace(/_/g, ' ')}:</span>
                                    <span className="font-medium text-emerald-400">{value}</span>
                                  </div>
                                )
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-red-400 mb-2">
                            <AlertCircle className="w-5 h-5" />
                            <span className="font-medium">Błąd przywracania</span>
                          </div>
                          <p className="text-red-300 text-sm">{restoreResult.message}</p>
                        </div>
                      )}
                    </div>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                {!restoreResult ? (
                  <>
                    <AlertDialogCancel 
                      onClick={handleRestoreCancel}
                      className="bg-transparent border-[var(--crm-border)] text-[var(--crm-text-secondary)] hover:bg-[var(--crm-bg-btn-secondary)]"
                    >
                      Anuluj
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleRestoreConfirm}
                      disabled={restoring}
                      className="bg-amber-600 hover:bg-amber-700 text-[var(--crm-text-primary)]"
                    >
                      {restoring ? (
                        <>
                          <div className="w-4 h-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Przywracanie...
                        </>
                      ) : (
                        <>
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Tak, przywróć dane
                        </>
                      )}
                    </AlertDialogAction>
                  </>
                ) : (
                  <AlertDialogAction
                    onClick={handleRestoreCancel}
                    className="bg-[#FF6B35] hover:bg-[#E55A2B] text-white"
                  >
                    Zamknij
                  </AlertDialogAction>
                )}
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Additional Backup Suggestions */}
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-[#FF6B35]" />
                <div>
                  <CardTitle>Dodatkowe Opcje Backup</CardTitle>
                  <CardDescription className="text-[var(--crm-text-secondary)]">
                    Inne sposoby zabezpieczenia danych
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 bg-[var(--crm-bg-main)] rounded-lg border border-[var(--crm-border)]">
                  <h4 className="text-[var(--crm-text-primary)] font-medium mb-2">☁️ Backup w Chmurze</h4>
                  <p className="text-[var(--crm-text-secondary)] text-sm">
                    Możliwość integracji z Google Drive lub Dropbox dla automatycznego zapisywania kopii.
                  </p>
                  <Badge className="mt-2 bg-slate-600 text-[var(--crm-text-secondary)]">Planowane</Badge>
                </div>
                
                <div className="p-4 bg-[var(--crm-bg-main)] rounded-lg border border-[var(--crm-border)]">
                  <h4 className="text-[var(--crm-text-primary)] font-medium mb-2">📅 Automatyczny Backup</h4>
                  <p className="text-[var(--crm-text-secondary)] text-sm">
                    Codzienny backup o wybranej godzinie wysyłany na email lub do chmury.
                  </p>
                  <Badge className="mt-2 bg-slate-600 text-[var(--crm-text-secondary)]">Planowane</Badge>
                </div>
                
                <div className="p-4 bg-[var(--crm-bg-main)] rounded-lg border border-[var(--crm-border)]">
                  <h4 className="text-[var(--crm-text-primary)] font-medium mb-2">🔄 Import Danych</h4>
                  <p className="text-[var(--crm-text-secondary)] text-sm">
                    Przywracanie danych z pliku JSON w przypadku awarii lub przenoszenia systemu.
                  </p>
                  <Badge className="mt-2 bg-slate-600 text-[var(--crm-text-secondary)]">Planowane</Badge>
                </div>
                
                <div className="p-4 bg-[var(--crm-bg-main)] rounded-lg border border-[var(--crm-border)]">
                  <h4 className="text-[var(--crm-text-primary)] font-medium mb-2">📊 Eksport Raportów</h4>
                  <p className="text-[var(--crm-text-secondary)] text-sm">
                    Generowanie raportów PDF z wybranymi danymi za dowolny okres.
                  </p>
                  <Badge className="mt-2 bg-slate-600 text-[var(--crm-text-secondary)]">Planowane</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Session Security */}
          <Card className="bg-[var(--crm-bg-card)] border-[var(--crm-border)]">
            <CardHeader>
              <CardTitle className="text-[var(--crm-text-primary)] font-manrope flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#FF6B35]" />
                Bezpieczeństwo sesji
              </CardTitle>
              <CardDescription className="text-[var(--crm-text-secondary)]">Automatyczne wylogowanie po nieaktywności</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-[var(--crm-text-secondary)] text-sm">Czas nieaktywności do wylogowania</Label>
                <select value={sessionTimeout} onChange={e => setSessionTimeout(Number(e.target.value))}
                  data-testid="session-timeout-select"
                  className="mt-1 w-full bg-[var(--crm-bg-panel)] border border-[var(--crm-border)] text-[var(--crm-text-primary)] rounded-md p-2 text-sm">
                  <option value={1}>1 godzina</option>
                  <option value={2}>2 godziny</option>
                  <option value={4}>4 godziny</option>
                  <option value={8}>8 godzin</option>
                  <option value={12}>12 godzin</option>
                  <option value={24}>24 godziny</option>
                  <option value={0}>Bez limitu</option>
                </select>
              </div>
              <div>
                <Label className="text-[var(--crm-text-secondary)] text-sm">Czas ostrzeżenia przed wylogowaniem</Label>
                <select value={sessionWarning} onChange={e => setSessionWarning(Number(e.target.value))}
                  data-testid="session-warning-select"
                  className="mt-1 w-full bg-[var(--crm-bg-panel)] border border-[var(--crm-border)] text-[var(--crm-text-primary)] rounded-md p-2 text-sm">
                  <option value={1}>1 minuta</option>
                  <option value={5}>5 minut</option>
                  <option value={10}>10 minut</option>
                </select>
              </div>
              <Button onClick={async () => {
                  setSavingSession(true);
                  try {
                    await axios.put(`${API_URL}/settings/session`, { timeout_hours: sessionTimeout, warning_minutes: sessionWarning });
                    toast.success('Ustawienia sesji zapisane');
                  } catch { toast.error('Błąd zapisu'); }
                  finally { setSavingSession(false); }
                }}
                disabled={savingSession}
                data-testid="save-session-settings"
                className="bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white">
                {savingSession ? 'Zapisywanie...' : 'Zapisz ustawienia sesji'}
              </Button>
            </CardContent>
          </Card>

          {/* Navigation Customization */}
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)]">
            <CardHeader>
              <CardTitle className="text-[var(--crm-text-primary)] flex items-center gap-2"><Eye className="w-5 h-5 text-[#FF6B35]" />Wygląd i Nawigacja</CardTitle>
              <CardDescription className="text-[var(--crm-text-secondary)]">Pokaż lub ukryj zakładki w menu bocznym</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {(() => {
                const role = user?.role;
                const isB2B = role === 'handlowiec_b2b';
                const allTabs = [
                  { path: '/dashboard', label: 'Strona główna', icon: LayoutDashboard, protected: true },
                  ...(role !== 'montaz' && role !== 'duch' ? [{ path: '/leads', label: 'Leady', icon: Target }] : []),
                  { path: '/projects', label: isB2B ? 'Moje Zlecenia' : 'Zlecenia', icon: FolderKanban },
                  ...(role !== 'montaz' && role !== 'duch' ? [{ path: '/klienci', label: 'Klienci', icon: Contact }] : []),
                  ...(role !== 'montaz' && role !== 'duch' && !isB2B ? [{ path: '/kanban', label: 'Tablica', icon: LayoutGrid }] : []),
                  ...(role !== 'montaz' && role !== 'duch' ? [{ path: '/reports', label: 'Raporty', icon: BarChart3 }] : []),
                  ...(role === 'admin' || role === 'asystent_biura' ? [{ path: '/godziny', label: 'Godziny pracy', icon: UserCog }] : []),
                  ...(role === 'admin' ? [{ path: '/users', label: 'Użytkownicy', icon: UsersIcon }] : []),
                  { path: '/settings', label: 'Ustawienia', icon: SettingsIcon, protected: true },
                  { path: '/pomoc', label: 'Pomoc', icon: HelpCircle },
                ];

                const toggleTab = async (path) => {
                  const newHidden = hiddenTabs.includes(path)
                    ? hiddenTabs.filter(t => t !== path)
                    : [...hiddenTabs, path];
                  setHiddenTabs(newHidden);
                  updateUser({ hidden_tabs: newHidden });
                  try {
                    await axios.put(`${API_URL}/users/me/tabs`, { hidden_tabs: newHidden });
                  } catch { toast.error('Błąd zapisu'); }
                };

                return allTabs.map(tab => {
                  const Icon = tab.icon;
                  const isHidden = hiddenTabs.includes(tab.path);
                  return (
                    <div key={tab.path} className={`flex items-center justify-between py-2.5 px-3 rounded-lg transition-colors ${isHidden ? 'bg-slate-800/30 opacity-60' : 'bg-[var(--crm-bg-main)]'}`}
                      data-testid={`nav-toggle-${tab.path.replace('/', '')}`}>
                      <div className="flex items-center gap-3">
                        <Icon className={`w-4 h-4 ${isHidden ? 'text-[var(--crm-text-muted)]' : 'text-[#FF6B35]'}`} />
                        <span className={`text-sm font-medium ${isHidden ? 'text-[var(--crm-text-muted)] line-through' : 'text-[var(--crm-text-primary)]'}`}>{tab.label}</span>
                        {tab.protected && <Lock className="w-3 h-3 text-[var(--crm-text-muted)]" />}
                      </div>
                      <Switch
                        checked={!isHidden}
                        disabled={tab.protected}
                        onCheckedChange={() => toggleTab(tab.path)}
                        data-testid={`nav-switch-${tab.path.replace('/', '')}`}
                        className="data-[state=checked]:bg-[#FF6B35]"
                      />
                    </div>
                  );
                });
              })()}
            </CardContent>
          </Card>
        </div>
        )}
      </div>
    </Layout>
  );
};

export default Settings;
